import React, { useState } from "react";

import {
  CLUSTER_PALETTE,
  type PortfolioClusterKey,
} from "./portfolioClusterStyles";
import SecondarySignals from "./SecondarySignals";
import {
  SIGNAL_COPY,
  cardCta,
  resolveSignalCopy,
  resolveTemplate,
  templateFields,
} from "./signalCTAs";
import SignalGuideModal from "./SignalGuideModal";
import {
  isAlStale,
  type SignalCardAccountContext,
} from "./useSignalCardsWithAccountContext";
import { useSignalCtaRouter } from "./useSignalCtaRouter";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  recordSignalCtaClick,
  toggleGuideStep,
} from "../../Store/Reducers/SignalFeedSlice";
import type {
  SignalCard,
  SignalTier,
} from "../../Store/Reducers/SignalFeedSlice";

/** Derived from the consolidated registry in ./signalCTAs — kept as a flat
 * title-only map for PortfolioMapScatter.tsx/CompactActionCard.tsx, which
 * still import this export name. Not hand-maintained here anymore; edit
 * SIGNAL_COPY in ./signalCTAs instead. */
export const SIGNAL_TITLES: Record<string, string> = Object.fromEntries(
  Object.entries(SIGNAL_COPY).map(([key, entry]) => [key, entry.title])
);

/**
 * Pixel-matched to the mock's `.feed-card` (my-accounts-portfolio-v4.html
 * CSS lines 1668-1728, markup from `renderFeedCard()` lines 8010-8077).
 * `TIER_META` below intentionally keeps only what still varies by tier
 * outside the CSS-class values below (nothing — kept for callers that still
 * import it, e.g. it is not referenced by this file's own render anymore).
 */
export const TIER_META: Record<
  SignalTier,
  { label: string; border: string; badge: string }
> = {
  critical: { label: "Critical", border: "border-l-[#dc2626]", badge: "" },
  warning: { label: "Warning", border: "border-l-[#d97706]", badge: "" },
  opp: { label: "Opportunity", border: "border-l-[#0d9488]", badge: "" },
  maint: { label: "Maintenance", border: "border-l-[#9ca3af]", badge: "" },
};

/** mock `fcPill()` — signal pill variant class suffix per tier. */
const PILL_STYLE: Record<SignalTier, string> = {
  critical: "bg-[rgba(220,38,38,0.12)] text-[#dc2626]",
  warning: "bg-[rgba(217,119,6,0.12)] text-[#b45309]",
  opp: "bg-[rgba(13,148,136,0.12)] text-[#0d9488]",
  maint: "bg-[#f3f4f6] text-[#6b7280]",
};

const TIER_LABEL: Record<SignalTier, string> = {
  critical: "Critical",
  warning: "Warning",
  opp: "Opportunity",
  maint: "Maintenance",
};

interface ActionCardProps {
  card: SignalCard;
  context: SignalCardAccountContext;
  busy: boolean;
  onSnooze: (firingId: string, days: number) => void;
  onDismiss: (firingId: string) => void;
  withOrg: (path: string) => string;
  onOpenAssess: (account: { id: string; name: string }) => void;
  /** mock: renderFeedCard() only shows "Pre-call Brief →" when it isn't
   * already the primary CTA (fcPrimaryAction's 'ready-to-advance' entry).
   * Optional so CompactActionCard/other embeds that don't wire a brief
   * modal keep working unchanged. */
  onOpenBrief?: (card: SignalCard, context: SignalCardAccountContext) => void;
  /** Deep-link flash from Portfolio Map — border highlight on the card
   * itself (avoids ring/offset clipping inside overflow-x rails). */
  highlighted?: boolean;
  /** Signal Administration preview only: markers showing which setting
   * produced the button, the detail line and the awareness line. */
  markers?: {
    cta?: React.ReactNode;
    detail?: React.ReactNode;
    awareness?: React.ReactNode;
  };
  /** Parameter values for {{parameter-id}} placeholders. Defaults to the
   * feed's (values in force); Signal Administration passes its draft's. */
  parameters?: Record<string, number | string>;
  /** Set by a row laid out as a 4-row grid (ActionsTab): the card joins it as
   * a subgrid, so the header, signal text, "Surfaced because" strips and
   * buttons each start on the same line across every card in the row. */
  alignInRow?: boolean;
}

const ActionCard: React.FC<ActionCardProps> = ({
  card,
  context,
  busy,
  onSnooze,
  onDismiss,
  withOrg,
  onOpenAssess,
  onOpenBrief,
  highlighted = false,
  markers,
  parameters,
  alignInRow = false,
}) => {
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const feedParameters = useAppSelector(state => state.signalFeed.parameters);
  const fields = templateFields(
    card.evidence?.triggeredFields,
    parameters ?? feedParameters
  );
  const copy = resolveSignalCopy(card.signal_key, fields, card.cta?.card_copy);
  const why = copy.why || card.evidence?.conditionString;
  const { label: rawCtaLabel, kind: ctaKind, path: ctaPath } = cardCta(card);
  // An approved plan's button is its first step's title, which may hold
  // placeholders ({{al}}, {{al-stale-days}}) like the rest of the card.
  const ctaLabel = resolveTemplate(rawCtaLabel, fields);

  const dispatch = useAppDispatch();
  const routeCta = useSignalCtaRouter(withOrg, onOpenAssess);
  const openBrief = onOpenBrief ? () => onOpenBrief(card, context) : undefined;
  const guide = card.cta?.guide ?? null;
  const [guideOpen, setGuideOpen] = useState(false);

  const handleCTA = () => {
    void dispatch(
      recordSignalCtaClick({
        firingId: card.signal_firing_id,
        label: ctaLabel,
      })
    );
    // A published Action Guide explains the steps first; its main button
    // still goes where the CTA always went.
    if (guide) {
      setGuideOpen(true);
      return;
    }
    routeCta(card, ctaKind, ctaPath, openBrief);
  };

  // mock: `a.ah > 0` gates whether the cluster badge shows at all.
  const clusterKey = context.cluster as PortfolioClusterKey | null;
  const showClusterBadge =
    context.ah != null && context.ah > 0 && clusterKey != null;
  const clusterPalette = clusterKey ? CLUSTER_PALETTE[clusterKey] : null;

  // Chips read "AL 2" / "RL 3", and "AL ?" / "RL ?" with an "Unassessed"
  // tooltip when there's no reading (Sushant, 2026-10-28; the app-wide
  // convention). No AL on file is a data gap (tinted, no icon), not
  // staleness; "stale" means an assessment older than the al-stale-days rule
  // parameter — amber tint, text only, no icon.
  // No alTrend arrow: no real-API source for the mock's client-parsed trend.
  const alUnassessed = context.al == null;
  const alLabel = alUnassessed ? "AL ?" : `AL ${context.al}`;
  const alStale =
    !alUnassessed &&
    isAlStale(context.alStaleDays, parameters ?? feedParameters);

  const showBhConcern = context.bhClass === "concern";
  const showRenewal = context.renewalDays != null && context.renewalDays < 180;
  const renewalUrgent = (context.renewalDays ?? Infinity) < 60;

  // No "→ AL{target}" pill and no "+N to close" gap: the Target AL stays
  // off the card (Sushant, 2026-10-26).
  const showRlCond =
    context.rlConditions === "cautious" || context.rlConditions === "blocked";

  return (
    <div
      className={`${alignInRow ? "row-span-4 grid grid-rows-subgrid" : "flex h-full flex-col"} w-[320px] min-h-[200px] shrink-0 overflow-hidden rounded-xl border-2 bg-white shadow-[0_2px_16px_rgba(0,0,0,0.08)] ${
        highlighted ? "border-purple-500" : "border-[#e5e7eb]"
      }`}
    >
      {/* .card-header-row */}
      <div className="flex flex-col gap-[5px] px-4 py-[14px]">
        <div className="flex flex-wrap items-center gap-[7px]">
          {showClusterBadge && clusterPalette && (
            <span
              className="rounded-[4px] px-[7px] py-[2px] text-[10px] font-bold uppercase tracking-[0.06em]"
              style={{
                background: clusterPalette.bg,
                color: clusterPalette.text,
              }}
            >
              {clusterPalette.label}
            </span>
          )}
          {showBhConcern && (
            <span
              className="inline-flex shrink-0 items-center gap-1 rounded-[4px] px-[7px] py-[2px] text-[10px] font-bold"
              style={{ background: "rgba(220,38,38,.1)", color: "#dc2626" }}
              title="Business Health: Concern"
            >
              ⚑ Concern
            </span>
          )}
          {showRenewal && (
            <span
              className={`ml-auto text-[11px] font-semibold ${renewalUrgent ? "text-[#dc2626]" : "text-[#9ca3af]"}`}
            >
              {(context.renewalDays ?? 0) < 0
                ? `Renewal overdue: ${Math.abs(context.renewalDays ?? 0)}d !`
                : `Renewal: ${context.renewalDays}d${renewalUrgent ? " !" : ""}`}
            </span>
          )}
        </div>
        <div className="text-[15px] font-extrabold leading-tight text-[#0f1623]">
          {card.account_name}
        </div>
        <div className="flex flex-wrap items-center gap-[6px]">
          <span
            className="inline-flex items-center gap-1 rounded-[4px] px-[7px] py-[2px] text-[11px] font-semibold"
            style={
              alStale || alUnassessed
                ? { background: "rgba(217,119,6,.12)", color: "#b45309" }
                : { background: "#F3F4F6", color: "#374151" }
            }
            title={
              alStale
                ? `Last assessed ${context.alStaleDays} days ago`
                : alUnassessed
                  ? "Unassessed"
                  : undefined
            }
          >
            {alLabel}
            {alStale && ` · stale ${context.alStaleDays}d`}
          </span>
          <span
            className="rounded-[4px] bg-[#F3F4F6] px-[7px] py-[2px] text-[11px] font-semibold text-[#374151]"
            title={context.rl != null ? undefined : "Unassessed"}
          >
            {context.rl != null ? `RL ${context.rl}` : "RL ?"}
          </span>
          {showRlCond && (
            <span
              className="shrink-0 rounded-[4px] px-[6px] py-[2px] text-[10px] font-bold capitalize"
              style={
                context.rlConditions === "blocked"
                  ? { background: "rgba(220,38,38,0.10)", color: "#dc2626" }
                  : { background: "rgba(217,119,6,0.10)", color: "#d97706" }
              }
            >
              {context.rlConditions}
            </span>
          )}
        </div>
      </div>

      {/* .card-signal-body — in a row the signal text block takes the row's
          tallest height, so the strips below it start on one line. */}
      <div className="flex flex-col gap-[7px] border-y border-[#e5e7eb] bg-black/[0.025] px-5 pb-4 pt-[10px]">
        <span
          className={`self-start rounded-[4px] px-2 py-[2px] text-[10px] font-extrabold uppercase tracking-[0.07em] ${PILL_STYLE[card.tier]}`}
        >
          {TIER_LABEL[card.tier]}
        </span>
        <span className="text-[12.5px] leading-[1.5] text-[#374151]">
          <strong>{copy.title}</strong>
          {copy.text ? <> — {copy.text}</> : null}
        </span>
      </div>
      {/* flex-1 (stand-alone) / the row's 1fr track (in a row) absorbs leftover
          height, so the buttons stay bottom-aligned across a row */}
      <div className="flex min-h-0 flex-1 flex-col">
        {why && (
          <div className="flex items-start gap-[5px] bg-[#f3f4f6] px-5 py-2 text-[11px] italic text-[#9ca3af]">
            ℹ Surfaced because: {why}
          </div>
        )}
        <SecondarySignals card={card} />
        {card.cta?.awareness_note && (
          <div className="bg-sky-50 px-5 py-2 text-[11px] text-sky-900">
            {card.cta.awareness_note}
            {markers?.awareness}
          </div>
        )}
      </div>

      {/* .card-actions — pinned to bottom so Mark/Snooze line up across the row */}
      <div className="mt-auto flex flex-col gap-2.5 border-t border-[#e5e7eb] px-5 pb-4 pt-3.5">
        {card.cta?.detail && (
          <p className="m-0 text-[11.5px] leading-[1.45] text-[#4b5563]">
            {resolveTemplate(card.cta.detail, fields)}
            {markers?.detail}
          </p>
        )}
        <div className="relative flex flex-col gap-2">
          {markers?.cta && (
            <span className="absolute -right-2 -top-2 z-10">{markers.cta}</span>
          )}
          <button
            type="button"
            onClick={handleCTA}
            title={ctaLabel}
            className="inline-flex h-8 w-full items-center justify-center rounded-md bg-brand px-3 text-xs font-semibold text-white hover:bg-brand-dark"
          >
            <span className="truncate">{ctaLabel}</span>
          </button>
          {/* .fc-btn-secondary — mock: shown unless the primary CTA already
           *is* "Pre-call Brief →" (renderFeedCard, mock line ~8062). */}
          {/* Hidden when the primary CTA already opens the brief. */}
          {onOpenBrief && ctaKind !== "brief" && (
            <button
              type="button"
              onClick={() => onOpenBrief(card, context)}
              className="inline-flex h-8 w-full items-center justify-center whitespace-nowrap rounded-md border border-[#e5e7eb] bg-white px-3 text-xs font-medium text-[#0f1623] hover:border-brand-light"
            >
              Pre-call Brief →
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={busy}
            onClick={() => onDismiss(card.signal_firing_id)}
            className="bg-none py-0.5 text-xs text-[#9ca3af] hover:text-[#374151] disabled:cursor-not-allowed disabled:opacity-50"
          >
            ✓ Mark as handled
          </button>
          {/* Critical cards ignore snooze (§7.3) — don't offer a button
              that would silently do nothing. */}
          {card.tier !== "critical" && (
            <div className="relative inline-block">
              <button
                type="button"
                disabled={busy}
                onClick={() => setSnoozeOpen(v => !v)}
                className="bg-none py-0.5 text-xs text-[#9ca3af] hover:text-[#374151] disabled:cursor-not-allowed disabled:opacity-50"
              >
                ⏱ Snooze ▾
              </button>
              {snoozeOpen && (
                <div className="absolute bottom-[calc(100%+4px)] left-0 z-[200] min-w-[150px] rounded-lg border border-[#e5e7eb] bg-white py-1 shadow-[0_4px_16px_rgba(0,0,0,0.12)]">
                  {[
                    { label: "3 days", days: 3 },
                    { label: "7 days", days: 7 },
                  ].map(opt => (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => {
                        onSnooze(card.signal_firing_id, opt.days);
                        setSnoozeOpen(false);
                      }}
                      className="block w-full px-[14px] py-[7px] text-left text-xs text-[#374151] hover:bg-gray-50"
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {guide && (
        <SignalGuideModal
          isOpen={guideOpen}
          onClose={() => setGuideOpen(false)}
          guide={guide}
          accountName={card.account_name}
          signalTitle={copy.title}
          tier={card.tier}
          fields={fields}
          planBucket={card.cta?.plan?.bucket ?? null}
          // An approved action plan's steps carry their own links; its
          // button already opened them, so no second main button.
          primaryLabel={card.cta?.plan ? undefined : ctaLabel}
          onPrimary={() => {
            setGuideOpen(false);
            routeCta(card, ctaKind, ctaPath, openBrief);
          }}
          onStepLink={link => {
            setGuideOpen(false);
            routeCta(card, link.kind, link.path, openBrief);
          }}
          progress={card.guide_progress ?? []}
          onToggleStep={(step, title, done) =>
            void dispatch(
              toggleGuideStep({
                firingId: card.signal_firing_id,
                step,
                title,
                done,
              })
            )
          }
        />
      )}
    </div>
  );
};

export default ActionCard;
